import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { Company, CompanyCode } from '@dcc-erp/shared';

import { api } from '../api';
import {
  clearPendingLoginCompany,
  readPendingLoginCompany,
  resolvePendingLoginCompanyId,
} from '../auth/pending-company';
import { useAuth } from '../auth/useAuth';

// Companies are seeded constants (DCC / TTOP / RCI) — they essentially never change
// at runtime, so a 1-hour staleTime avoids refetching while still letting an explicit
// invalidation propagate when seeds are reloaded in dev.
export function useCompanies() {
  return useQuery({
    queryKey: ['companies'],
    queryFn: () => api<Company[]>('/companies'),
    staleTime: 60 * 60_000,
  });
}

// The id is what all per-company endpoints need on the URL (UUID, not the code).
// Returns undefined while auth is loading or the user has no allowed companies;
// consumers should gate queries via `enabled: !!companyId`.
export function useActiveCompanyId(): string | undefined {
  const { activeCompanyId } = useAuth();
  return activeCompanyId ?? undefined;
}

export function useActiveCompany(): Company | undefined {
  const { data: companies } = useCompanies();
  const id = useActiveCompanyId();
  if (!id || !companies) return undefined;
  return companies.find((c) => c.id === id);
}

// Code is derived from the active company row; fall back to 'DCC' until the
// companies list resolves so prototype styling (data-company) has a sane default.
export function useActiveCompanyCode(): CompanyCode {
  const company = useActiveCompany();
  return company?.code ?? 'DCC';
}

// Apply the company chosen on the login screen once auth + the companies list
// have resolved, and report whether that resolution is done so the caller can
// gate rendering until then — this prevents a one-frame flash of the previous /
// default company (e.g. DCC) before the chosen one is applied. The login screen
// can only stash the company *code* (the UUID doesn't exist client-side
// pre-auth); we map it to an allowed company id here and make it active.
// Single-use: an unknown/disallowed code, or a failed /companies fetch, simply
// releases the gate and falls through to the default. Mount once on an authed
// surface (AuthedShell).
export function useApplyPendingLoginCompany(): { resolved: boolean } {
  const { user, activeCompanyId, setActiveCompanyId } = useAuth();
  const { data: companies, isError } = useCompanies();
  // Snapshot the stash once at mount: it drives the gate, the loader theme, and
  // the resolution. `resolved` starts true when nothing was stashed, so returning
  // users and in-app navigation are never gated.
  const [pendingCode] = useState(() => readPendingLoginCompany());
  const [resolved, setResolved] = useState(() => pendingCode == null);

  // Paint the gate in the chosen company's theme so it never flashes the default
  // (DCC) before the active company flips — the stash IS the company code, so no
  // id mapping is needed here.
  useEffect(() => {
    if (resolved || !pendingCode || typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-company', pendingCode);
  }, [resolved, pendingCode]);

  useEffect(() => {
    if (resolved || !user) return;
    if (isError) {
      // Couldn't load /companies — release the gate instead of blocking forever.
      clearPendingLoginCompany();
      setResolved(true);
      return;
    }
    if (!companies) return; // still loading
    clearPendingLoginCompany();
    const id = resolvePendingLoginCompanyId(pendingCode, companies, user.allowedCompanyIds);
    if (id && id !== activeCompanyId) setActiveCompanyId(id);
    setResolved(true);
  }, [resolved, user, companies, isError, pendingCode, activeCompanyId, setActiveCompanyId]);

  return { resolved };
}
